import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV, COMPANY } from "../lib/content";
import { usePortalAuth } from "../portal/PortalAuth";

function Logo({ light }) {
  const src = light ? "/brand/mmedia-logo-light.png" : "/brand/mmedia-logo-dark.png";
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center group">
      <img
        src={src}
        alt="M Media Corp"
        className="h-12 md:h-14 w-auto select-none transition-opacity duration-300 group-hover:opacity-80"
        draggable="false"
      />
    </Link>
  );
}

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { user } = usePortalAuth() || {};

  // If logged in as a client (Studio purchaser), surface "My Library".
  // For team/super_admin, point to the dashboard.
  const memberDest =
    user && user.role === "client"
      ? { to: "/library", label: "My Library →" }
      : user && (user.role === "team" || user.role === "super_admin")
      ? { to: "/portal/dashboard", label: "Dashboard →" }
      : { to: "/portal", label: "Client Portal →" };

  // Pages that have a dark hero at the top of the page (so the transparent nav
  // sits over a dark background and needs the LIGHT logo until scrolled).
  const darkHeroRoutes = ["/", "/matter"];
  const onDarkHero = darkHeroRoutes.includes(loc.pathname) && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-cream/90 backdrop-blur-md border-b border-ink/10" : "bg-transparent"}`}
      >
        <div className="container-edge flex items-center justify-between py-4">
          <Logo light={onDarkHero} />
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `nav-link ${onDarkHero ? "text-cream" : "text-ink"} ${isActive ? "active" : ""}`}
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/library/login"
              className={`overline px-3 py-1.5 transition-colors ${onDarkHero ? "text-cream hover:opacity-70" : "text-ink hover:opacity-70"}`}
              data-testid="nav-my-shelf"
            >
              My Shelf →
            </Link>
            <Link
              to={memberDest.to}
              className={`overline border px-3 py-1.5 transition-all ${
                onDarkHero
                  ? "border-cream/40 text-cream hover:bg-cream hover:text-ink"
                  : "border-ink/40 text-ink hover:bg-ink hover:text-cream"
              }`}
              data-testid="nav-portal"
            >
              {memberDest.label}
            </Link>
          </div>
          <button
            className={`lg:hidden p-2 -mr-2 ${onDarkHero ? "text-cream" : "text-ink"}`}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-open-menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      {/* Fullscreen Mobile Menu */}
      <div
        className={`fixed inset-0 z-[60] bg-ink text-cream transition-all duration-500 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        data-testid="mobile-menu"
      >
        <div className="container-edge flex items-center justify-between py-4">
          <Logo light />
          <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-close-menu" className="text-cream p-2 -mr-2">
            <X size={26} />
          </button>
        </div>
        <div className="container-edge mt-12 flex flex-col gap-6">
          {NAV.map((n, i) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mnav-${n.label.toLowerCase()}`}
              className="font-display uppercase text-5xl sm:text-6xl text-cream hover:text-cream/60 transition-colors"
              style={{ transitionDelay: `${i * 40}ms` }}
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}

          {/* My Shelf quick-link */}
          <Link
            to="/library/login"
            data-testid="mnav-my-shelf"
            className="mt-6 border border-cream/40 hover:bg-cream hover:text-ink transition-all px-5 py-4 flex items-center justify-between"
          >
            <div>
              <div className="overline text-cream/55 mb-1">Bought before?</div>
              <div className="font-display uppercase text-3xl">My Shelf</div>
            </div>
            <span className="overline">→</span>
          </Link>

          {/* Portal / Library — members' entrance */}
          <Link
            to={memberDest.to}
            data-testid="mnav-portal"
            className="mt-8 border border-cream/40 hover:bg-cream hover:text-ink transition-all px-5 py-4 flex items-center justify-between"
          >
            <div>
              <div className="overline text-cream/55 mb-1 group-hover:text-ink/60">Members&apos; Entrance</div>
              <div className="font-display uppercase text-3xl">{memberDest.label.replace(" →", "")}</div>
            </div>
            <span className="overline">→</span>
          </Link>

          <div className="mt-10 overline text-cream/60">{COMPANY.address}</div>
          <a href={`mailto:${COMPANY.email}`} className="overline text-cream/80 py-2 block">{COMPANY.email}</a>
        </div>
      </div>
    </>
  );
}
